import { Link } from "react-router-dom";
import { FAQS } from "../data/content";
import Reveal from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo from "../components/Seo";
import { FAQS as FAQS_DATA } from "../data/content";

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS_DATA.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const STEPS = [
  { n: "01", t: "Assess", d: "We diagnose where growth is being restricted — across strategy, marketing, brand, customer experience, and operations. Nothing is prescribed until this is understood." },
  { n: "02", t: "Strategize", d: "We build a customized plan based on the real bottleneck. Priorities, sequencing, and measurable milestones." },
  { n: "03", t: "Execute", d: "We implement — marketing, brand systems, websites, campaigns, SOPs, or operations — with clear scope and defined deliverables." },
  { n: "04", t: "Measure", d: "We track what actually changed. Real metrics, not vanity numbers. Reviewed on a defined cadence." },
  { n: "05", t: "Grow", d: "We refine the strategy as the business scales. Continuous optimization is how compounding businesses are built." },
];

export default function HowWeHelp() {
  return (
    <div data-testid="page-how">
      <Seo
        title="How We Help — Our 5-Step Consulting Process"
        description="Our consulting process: Discovery, Business Assessment, Strategy, Implementation, and Optimization — designed to move Dallas–Fort Worth businesses forward."
        path="/how-we-help"
        jsonLd={FAQ_LD}
      />
      <section className="bg-white pt-20 pb-10">
        <div className="container-wide max-w-4xl">
          <div className="overline mb-6">How We Help</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="how-page-headline">
            The Mogul Method™ — Assess. Strategize. Execute. Measure. Grow.
          </h1>
          <p className="mt-6 text-lg text-[#4B5563] leading-relaxed">
            One methodology every engagement runs through. We don't start with a service — we start with the business. Once we understand what's actually restricting growth, the right strategy and execution follow.
          </p>
        </div>
      </section>

      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-16 md:py-24 grid lg:grid-cols-5 gap-6" data-testid="how-steps">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 60}>
              <div className="bg-white border-t-2 border-gold p-8 h-full" data-testid={`how-step-${s.n}`}>
                <div className="font-serif text-3xl text-gold leading-none">{s.n}</div>
                <h3 className="mt-4 font-serif text-xl">{s.t}</h3>
                <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="container-wide py-16 md:py-24 max-w-3xl">
          <div className="overline mb-4">Frequently Asked Questions</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">Answers to common questions.</h2>
          <div className="mt-10">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-medium text-[#0A0A0A] hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#4B5563] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="mt-12">
            <Link to="/growth-assessment" className="btn btn-primary" data-testid="how-final-cta">Get My Free Growth Assessment</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
